/**
 * SKU Description Generator
 *
 * Generates human-readable descriptions from SKU configuration values.
 * Used to create consistent, descriptive labels for SKUs across the UI.
 */

/**
 * Matrix/Cin7-style SKU display name: "{product} - {format}".
 *
 * @param {Object} opts
 * @param {string} [opts.productName] - Parent beer/product name
 * @param {string} opts.containerType - 'can' | 'bottle' | 'keg'
 * @param {string} opts.unitType - Unit key (e.g. '330', '330b', '30000')
 * @param {string} [opts.packType] - e.g. '1', '4', '6'
 * @param {string} [opts.caseType] - e.g. '24x1', '6x4'
 * @returns {string}
 *
 * @example
 * generateMatrixSkuName({
 *   productName: 'Bliss', containerType: 'can', unitType: '330',
 *   packType: '1', caseType: '24x1',
 * })
 * // → 'Bliss - 24 x 330ml cans'
 *
 * @example
 * generateMatrixSkuName({
 *   productName: 'Bliss', containerType: 'can', unitType: '330',
 *   packType: '4', caseType: '6x4',
 * })
 * // → 'Bliss - 6x4 pack of 330ml cans'
 */
export function generateMatrixSkuName({
  productName,
  containerType,
  unitType,
  packType,
  caseType,
}) {
  if (!containerType || !unitType) return '';

  const unitKey = String(unitType).replace(/b$/i, '');
  let skuPart;

  if (containerType === 'keg') {
    const litres = parseInt(unitType, 10) / 1000;
    skuPart = `${litres}L Keg`;
  } else {
    const containerWord = containerType === 'can' ? 'cans' : 'bottles';

    if (!caseType) {
      skuPart = `${unitKey}ml`;
    } else {
      const caseParts = caseType.split('x');
      if (caseParts.length !== 2) {
        skuPart = `${unitKey}ml`;
      } else {
        const [casePacks, unitsPerPack] = caseParts.map(Number);

        if (!packType || packType === '1' || unitsPerPack === 1) {
          skuPart = `${casePacks} x ${unitKey}ml ${containerWord}`;
        } else {
          skuPart = `${casePacks}x${unitsPerPack} pack of ${unitKey}ml ${containerWord}`;
        }
      }
    }
  }

  return productName ? `${productName} - ${skuPart}` : skuPart;
}

/**
 * Strip parent product prefix from a full SKU name for compact UI (tabs, Home).
 * Supports "Product - …" and legacy "Product …".
 *
 * @param {string} name - Full SKU name
 * @param {string} productName - Parent product name
 * @returns {string}
 */
export function stripProductSkuShortLabel(name, productName) {
  if (name == null || name === '') return '';
  if (!productName) return name;
  const dashPrefix = `${productName} - `;
  if (typeof name === 'string' && name.startsWith(dashPrefix)) {
    return name.slice(dashPrefix.length);
  }
  const legacyPrefix = `${productName} `;
  if (typeof name === 'string' && name.startsWith(legacyPrefix)) {
    return name.slice(legacyPrefix.length);
  }
  return name;
}

/**
 * Compute a SKU display name after the parent product (beer) name changes.
 * Mirrors backend `computeRenamedSkuName` in rename-sku-name-for-product-rename.js.
 *
 * @param {string|null|undefined} previousProductName
 * @param {string|null|undefined} newProductName
 * @param {string|null|undefined} skuName
 * @returns {string|null} The new SKU name, or null if no change should be applied.
 */
export function renameSkuNameForProductRename(previousProductName, newProductName, skuName) {
  if (skuName == null || skuName === '') return null;

  const prev = previousProductName != null ? String(previousProductName) : '';
  const next = newProductName != null ? String(newProductName) : '';
  const trimmedSku = String(skuName);

  if (prev === next) return null;

  if (prev) {
    const dashPrefix = `${prev} - `;
    if (trimmedSku.startsWith(dashPrefix)) {
      const rest = trimmedSku.slice(dashPrefix.length);
      return next ? `${next} - ${rest}` : rest;
    }

    const legacySpacePrefix = `${prev} `;
    if (trimmedSku.startsWith(legacySpacePrefix)) {
      const rest = trimmedSku.slice(legacySpacePrefix.length);
      return next ? `${next} ${rest}` : rest;
    }
  }

  if (next) {
    const newPrefix = `${next} - `;
    if (trimmedSku.startsWith(newPrefix)) {
      return null;
    }
    return `${newPrefix}${trimmedSku}`;
  }

  if (prev) {
    const dashPrefix = `${prev} - `;
    if (trimmedSku.startsWith(dashPrefix)) {
      return trimmedSku.slice(dashPrefix.length);
    }
    const legacySpacePrefix = `${prev} `;
    if (trimmedSku.startsWith(legacySpacePrefix)) {
      return trimmedSku.slice(legacySpacePrefix.length);
    }
  }

  return null;
}

/**
 * Generates a human-readable description from SKU configuration.
 *
 * @param {Object} sku - The SKU object containing unitType, packType, caseType, containerType
 * @returns {string} Human-readable description
 *
 * @example
 * // 6 x 4-Packs of 330ml cans (24 total)
 * generateSkuDescription({ unitType: '330', packType: '4', caseType: '6x4', containerType: 'can' })
 * // → "6 x 4-Pack x 330ml Can (24 total)"
 *
 * @example
 * // 24 loose 330ml cans
 * generateSkuDescription({ unitType: '330', packType: '', caseType: '24x1', containerType: 'can' })
 * // → "24 x 330ml Can"
 *
 * @example
 * // 4 x 6-Packs of 440ml cans (24 total)
 * generateSkuDescription({ unitType: '440', packType: '6', caseType: '4x6', containerType: 'can' })
 * // → "4 x 6-Pack x 440ml Can (24 total)"
 */
export function generateSkuDescription(sku) {
  const {
    unitType,
    packType,
    caseType,
    containerType,
  } = sku || {};

  if (!unitType || !caseType) return '';

  // Capitalize container type, default to 'Can'
  const container = containerType
    ? containerType.charAt(0).toUpperCase() + containerType.slice(1)
    : 'Can';

  const unitLabel = `${unitType}ml ${container}`;

  // Parse caseType (e.g., '6x4', '24x1')
  const parts = caseType.split('x');
  if (parts.length !== 2) return unitLabel;

  const [casePacks, packUnits] = parts.map(Number);

  // Check if there's an intermediate pack layer
  if (packType && packType !== '') {
    const packSize = parseInt(packType, 10);
    const totalUnits = casePacks * packSize;
    return `${casePacks} x ${packType}-Pack x ${unitLabel} (${totalUnits} total)`;
  }

  // Loose units in case (e.g., '24x1')
  const totalUnits = casePacks * packUnits;
  return `${totalUnits} x ${unitLabel}`;
}

/**
 * Gets an improved label for caseType values.
 * Fixes the duplicate "24-Pack" issue in the original config.
 *
 * @param {string} caseType - The caseType value (e.g., '6x4', '24x1')
 * @param {string} packType - The packType value (e.g., '4', '6', '')
 * @returns {string} Human-readable label
 *
 * @example
 * getCaseTypeLabel('6x4', '4') // → "6 x 4-Pack (24 total)"
 * getCaseTypeLabel('24x1', '') // → "24 x Loose"
 */
export function getCaseTypeLabel(caseType, packType = '') {
  if (!caseType) return '';

  const parts = caseType.split('x');
  if (parts.length !== 2) return caseType;

  const [casePacks, packUnits] = parts.map(Number);

  if (packType && packType !== '') {
    const packSize = parseInt(packType, 10);
    const totalUnits = casePacks * packSize;
    return `${casePacks} x ${packType}-Pack (${totalUnits} total)`;
  }

  if (packUnits === 1) {
    return `${casePacks} x Loose`;
  }

  const totalUnits = casePacks * packUnits;
  return `${casePacks} x ${packUnits}-Pack (${totalUnits} total)`;
}

/**
 * Container type options for SKU editor
 */
export const containerTypeOptions = [
  { label: 'Can', value: 'can' },
  { label: 'Bottle', value: 'bottle' },
  { label: 'Keg', value: 'keg' },
];

/**
 * Normalized container type from API (snake_case) or form (camelCase).
 * @param {Object} sku
 * @returns {string}
 */
export function getSkuContainerType(sku) {
  if (!sku || typeof sku !== 'object') return '';
  const t = sku.containerType ?? sku.container_type;
  return typeof t === 'string' ? t.toLowerCase().trim() : '';
}

function getSkuContainerSortRank(sku) {
  const t = getSkuContainerType(sku);
  if (t === 'can') return 0;
  if (t === 'keg') return 1;
  if (t === 'bottle') return 2;
  return 3;
}

/**
 * Unit volume for ordering within a container (ml). Kegs use unit key as ml (e.g. 19500).
 * @param {Object} sku
 * @returns {number}
 */
function getSkuUnitVolumeMl(sku) {
  if (!sku || typeof sku !== 'object') return 0;
  const raw = sku.unitType ?? sku.unit_type;
  const s = raw == null ? '' : String(raw);
  if (!s) return 0;
  const n = parseInt(s.replace(/b$/i, ''), 10);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Sorts SKUs for sub-tab order: cans, then kegs, then bottles, then unknown.
 * Within a container type, sorts by format size (unit volume ml) ascending, then by Matrix key.
 * @param {Array} skus
 * @returns {Array<Object>}
 */
export function sortSkusByContainerType(skus) {
  if (!Array.isArray(skus) || skus.length === 0) return [];
  return [...skus].sort((a, b) => {
    const d = getSkuContainerSortRank(a) - getSkuContainerSortRank(b);
    if (d !== 0) return d;
    const va = getSkuUnitVolumeMl(a);
    const vb = getSkuUnitVolumeMl(b);
    if (va !== vb) return va - vb;
    const ka = Number(a.key) || 0;
    const kb = Number(b.key) || 0;
    return ka - kb;
  });
}

/**
 * Section heading for the first SKU tab in each container group.
 * @param {Object} sku
 * @returns {string}
 */
export function getSkuContainerGroupLabel(sku) {
  const t = getSkuContainerType(sku);
  if (t === 'can') return 'Cans';
  if (t === 'bottle') return 'Bottles';
  if (t === 'keg') return 'Kegs';
  return 'Other';
}

/**
 * Creates default values for a new SKU.
 * Single source of truth for SKU field defaults.
 *
 * @param {Object} options - Options for creating default values
 * @param {number|string} options.productId - The parent product's Matrix ID
 * @param {number|string} options.productMondayId - The parent product's Monday ID
 * @returns {Object} Default SKU values object
 */
export function createDefaultSkuValues({ productId, productMondayId }) {
  return {
    // Database key (null for new SKUs)
    key: null,

    // Parent product references
    productMatrixId: productId || null,
    productMondayId: productMondayId ? Number(productMondayId) : null,

    // Monday integration
    mondayId: '',

    // Core identification
    name: '',
    cin7Sku: '',

    // Format configuration
    unitType: '',
    packType: '',
    caseType: '',
    containerType: '',
    isVenueSku: false,
    isLegacy: false,

    // Barcodes
    bnUnit: '',
    bnPack: '',
    bnCase: '',
    barcodeLegacy: '',

    // RRP (Recommended Retail Price)
    rrpUnit: '',
    rrpPack: '',
    rrpCase: '',

    // Trade Pricing (NZ)
    priceOn: '',
    priceOff: '',
    priceProgressive: '',

    // Trade Pricing (AU)
    priceAu: '',
    priceVicSa: '',
    priceQldWa: '',

    // Venue Pricing (NZ)
    priceVenuePerLitre: '',
    priceBar: '',
    pricePint: '',
    priceHalfPint: '',
    price150ml: '',
    price500ml: '',

    // Retailer Integration
    foodstuffsArticleNi: '',
    foodstuffsArticleSi: '',

    // Miscellaneous
    formatDescription: '',
    dearCode: '',
  };
}

export default {
  generateSkuDescription,
  getCaseTypeLabel,
  containerTypeOptions,
  getSkuContainerType,
  sortSkusByContainerType,
  getSkuContainerGroupLabel,
  createDefaultSkuValues,
  generateMatrixSkuName,
  stripProductSkuShortLabel,
  renameSkuNameForProductRename,
};
